import type { CalendarEvent, CalendarEventStatus } from '../types.js';
import { eventDurationMinutes, isDateOnlyISO } from './dateUtils.js';

/**
 * Derive a runtime status when the event itself doesn't carry one.
 * - Cancelled rows always stay cancelled.
 * - All-day rows: live on the day, past after, scheduled before.
 * - Timed rows: live between startsAt and (endsAt ?? startsAt + 1h).
 *
 * The returned status is suitable for filter facets and badge rendering.
 */
export function deriveEventStatus(
  event: CalendarEvent,
  now: Date = new Date(),
): CalendarEventStatus {
  if (event.status === 'cancelled') return 'cancelled';
  const start = new Date(event.startsAt);
  if (Number.isNaN(start.getTime())) return event.status ?? 'scheduled';

  if (event.allDay === true || isDateOnlyISO(event.startsAt)) {
    const day = event.startsAt.slice(0, 10);
    const todayKey =
      `${now.getFullYear()}-` +
      `${String(now.getMonth() + 1).padStart(2, '0')}-` +
      String(now.getDate()).padStart(2, '0');
    if (day === todayKey) return 'live';
    return day < todayKey ? 'past' : 'scheduled';
  }

  const dur = eventDurationMinutes(event.startsAt, event.endsAt) ?? 60;
  const end = new Date(start.getTime() + dur * 60_000);
  const t = now.getTime();
  if (t < start.getTime()) return 'scheduled';
  if (t >= end.getTime()) return 'past';
  return 'live';
}

const STATUS_ORDER: readonly CalendarEventStatus[] = ['scheduled', 'live', 'past', 'cancelled'];

export const ALL_STATUSES = STATUS_ORDER;
